import type { WalletDegenHoursDto } from '@root/shared/api/ServicesApi'

/**
 * When a wallet makes its money, by the hour of its own week.
 *
 * A trader's edge is not spread evenly across a week: the same wallet that is
 * patient on a Tuesday morning chases on a Friday night. A hundred and
 * sixty-eight squares say that at a glance, where a list of trades does not.
 *
 * The server sends only the hours the wallet traded in. The difference
 * between an hour it lost money in and an hour it was asleep is the whole
 * point, so an hour that is missing stays missing here rather than becoming a
 * zero.
 */
export type Cell = {
  /** 1 = Monday … 7 = Sunday. */
  day: number
  /** 0 … 23, in the reader's own timezone. */
  hour: number
  trades: number
  /** Realised profit in that hour, in dollars. */
  pnl: number
  wins: number
}

export type DegenHours = {
  cells: ReadonlyArray<Cell>
  timezone: string
  days: number
}

export const DAYS = [1, 2, 3, 4, 5, 6, 7] as const
export const HOURS = Array.from({ length: 24 }, (_, hour) => hour)

export const fromDto = (dto: WalletDegenHoursDto): DegenHours => ({
  cells: dto.cells.map((cell) => ({
    day: cell.day,
    hour: cell.hour,
    trades: cell.trades,
    pnl: cell.pnl,
    wins: cell.wins,
  })),
  timezone: dto.timezone,
  days: dto.days,
})

const keyOf = (day: number, hour: number) => `${day}:${hour}`

/** The grid, ready to draw: an hour with no trades is absent, not zero. */
export const byHour = (hours: DegenHours): Map<string, Cell> =>
  new Map(hours.cells.map((cell) => [keyOf(cell.day, cell.hour), cell]))

export const cellAt = (
  grid: Map<string, Cell>,
  day: number,
  hour: number,
): Cell | undefined => grid.get(keyOf(day, hour))

/**
 * What one trade in that hour made on average.
 *
 * The average, not the total, is what a square shows: an hour with four
 * hundred trades is not a better hour than one with four, it is a busier one,
 * and a heatmap of totals is a heatmap of when the wallet was awake.
 */
export const perTrade = (cell: Cell): number =>
  cell.trades === 0 ? 0 : cell.pnl / cell.trades

export const winRate = (cell: Cell): number =>
  cell.trades === 0 ? 0 : cell.wins / cell.trades

/**
 * How strongly to colour a square, from 0 to 1.
 *
 * Against the largest average either way, so the best and the worst hour are
 * both fully saturated and everything else is read against them. Scaling each
 * sign separately would make a wallet that lost a dollar once look as red as
 * one that lost a thousand.
 */
export const intensity = (value: number, extreme: number): number => {
  if (extreme <= 0) {
    return 0
  }
  return Math.min(Math.abs(value) / extreme, 1)
}

export const extremeOf = (cells: ReadonlyArray<Cell>): number =>
  cells.reduce((most, cell) => Math.max(most, Math.abs(perTrade(cell))), 0)

/** The hour that made the most money in total, which is what Axiom names. */
export const bestHour = (cells: ReadonlyArray<Cell>): Cell | undefined =>
  cells.reduce<Cell | undefined>(
    (best, cell) => (best === undefined || cell.pnl > best.pnl ? cell : best),
    undefined,
  )

export type Totals = {
  trades: number
  pnl: number
  wins: number
  /** Dollars made per trade across every hour. */
  perTrade: number
}

export const totalsOf = (cells: ReadonlyArray<Cell>): Totals => {
  const trades = cells.reduce((sum, cell) => sum + cell.trades, 0)
  const pnl = cells.reduce((sum, cell) => sum + cell.pnl, 0)
  const wins = cells.reduce((sum, cell) => sum + cell.wins, 0)

  return { trades, pnl, wins, perTrade: trades === 0 ? 0 : pnl / trades }
}
